import { useLingui } from "@lingui/react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Edit, Trash2 } from "lucide-react";
import { ActivityTypeFields } from "./ActivityTypeFields";
import { getActivityTypeLabels, type ActivityResponse } from "@/types/activities";
import { useImageUrl } from "@/hooks/useImage";
import { isImageId } from "@/utils/imageUtils";
import { formatDateTime } from "@/lib/format";

interface ActivityViewContentProps {
  activity: ActivityResponse;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Activity View Content Component
 *
 * Displays read-only view of activity with all details
 * Shows child info, activity type, time, recorded by, type-specific fields, and comments
 */
export function ActivityViewContent({
  activity,
  onEdit,
  onDelete,
}: ActivityViewContentProps) {
  const { i18n } = useLingui();

  // Resolve child avatar URL if it's an image ID
  const avatarUrl = activity.child?.avatar_url;
  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);
  const finalAvatarUrl = needsImageResolution ? resolvedImageUrl : avatarUrl;

  // Get child initials for avatar fallback
  const childInitials = activity.child
    ? `${activity.child.firstname?.[0] || ""}${activity.child.lastname?.[0] || ""}`.toUpperCase()
    : "?";

  // Format activity time
  const activityTime = activity.activity_time
    ? formatDateTime(activity.activity_time, i18n.locale)
    : "-";

  // Get recorded by name
  const recordedBy = activity.recorded_by
    ? `${activity.recorded_by.first_name || ""} ${activity.recorded_by.last_name || ""}`.trim()
    : "-";

  return (
    <div className="space-y-6">
      {/* Child Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={finalAvatarUrl || undefined} alt={`${activity.child?.firstname} ${activity.child?.lastname}`} />
              <AvatarFallback>{childInitials}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">
                {activity.child?.firstname} {activity.child?.lastname}
              </h3>
              <p className="text-sm text-muted-foreground">
                {activity.type ? getActivityTypeLabels(i18n)[activity.type] : ""}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Metadata */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-medium text-muted-foreground">
            {i18n._({ id: "activities.view.activityTime", message: "Activity Time" })}
          </div>
          <div className="text-base">{activityTime}</div>
        </div>
        <div>
          <div className="text-sm font-medium text-muted-foreground">
            {i18n._({ id: "activities.view.recordedBy", message: "Recorded By" })}
          </div>
          <div className="text-base">{recordedBy}</div>
        </div>
      </div>

      <Separator />

      {/* Type-Specific Fields */}
      <div>
        <h4 className="text-sm font-semibold mb-4">{i18n._({ id: "activities.view.details", message: "Details" })}</h4>
        <ActivityTypeFields type={activity.type ?? ""} data={activity.data || {}} />
      </div>

      {/* Comments */}
      {activity.comments && activity.comments.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-4">{i18n._({ id: "activities.view.comments", message: "Comments" })}</h4>
            <div className="space-y-3">
              {activity.comments.map((comment, index) => (
                <Card key={index}>
                  <CardContent className="pt-4">
                    <p className="text-sm">{comment.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onEdit}>
          <Edit className="mr-2 h-4 w-4" />
          {i18n._({ id: "activities.view.edit", message: "Edit" })}
        </Button>
        <Button variant="destructive" onClick={onDelete}>
          <Trash2 className="mr-2 h-4 w-4" />
          {i18n._({ id: "activities.view.delete", message: "Delete" })}
        </Button>
      </div>
    </div>
  );
}
